import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { addBreadcrumb } from '@sentry/react';
import { FLEX_SEARCH_CONFIGS, FLEX_PAGE_LIMIT } from 'app/pages/store/flexCodingSlice';
import { SearchType } from 'app/pages/store/agaveSlice';

import {
  FlexCodingCacheEntry as CacheEntry,
  flexInflightRequests as inflightRequests,
  FlexCodingFetchOutcome,
  getFlexCached as getCached,
  invalidateFlexOptionsCache,
  makeFlexCacheKey as makeCacheKey,
  setFlexCached as setCached,
} from './flexCodingDropdownCache';

export { invalidateFlexOptionsCache };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseFlexCodingDropdownOptions {
  codingType: SearchType;
  paidolId: string;
  cardGroupId?: string;
  selectedGlCode?: { id: string } | null;
  selectedProject?: { id: string } | null;
  enabled?: boolean;
}

interface UseFlexCodingDropdownReturn<T> {
  currentItems: T[];
  isSearching: boolean;
  isLoadingMore: boolean;
  // SEE-1392: a failed search used to be indistinguishable from one with no matches —
  // the catch below returned null and the caller set an empty item list, so the dropdown
  // read "No options found". `searchFailed` is what lets the dropdown say so instead.
  searchFailed: boolean;
  searchError: string | null;
  hasMore: boolean;
  searchTerm: string | undefined;
  handleSearch: (term: string) => void;
  handleClearSearch: () => void;
  handleFocus: () => void;
  handleLoadMore: () => Promise<void>;
  loadOptions: () => void;
}

export const useFlexCodingDropdown = <T>({
  codingType,
  paidolId,
  cardGroupId,
  selectedGlCode,
  selectedProject,
  enabled = true,
}: UseFlexCodingDropdownOptions): UseFlexCodingDropdownReturn<T> => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const hasInitializedRef = useRef(false);

  const [localItems, setLocalItems] = useState<T[]>([]);
  const [localNextToken, setLocalNextToken] = useState<string | null>(null);
  const [localTotal, setLocalTotal] = useState<number | undefined>(undefined);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [isLoadingMoreLocal, setIsLoadingMoreLocal] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const localFetchAbortRef = useRef<AbortController | null>(null);
  // Latest rendered items, for the load-more merge. fetchLocal cannot depend on
  // localItems directly without being rebuilt on every result, which would churn the
  // mount and filter-change effects.
  const localItemsRef = useRef<T[]>([]);

  useEffect(() => {
    localItemsRef.current = localItems;
  }, [localItems]);

  // Supersede whatever this instance has in flight.
  //
  // Clearing BOTH spinners is part of superseding, not an extra step. Every reset of
  // either flag is either on a path the aborted request will never reach, or sits in
  // the owner's `finally` behind `if (!controller.signal.aborted)` — deliberately, so
  // a stale request cannot clear the spinner of the request that replaced it. Nothing
  // else clears them. So whichever flag the aborted request was holding strands at
  // true for the life of the instance, and the two strand independently:
  //
  //   - a superseded load-more strands isLoadingMore, and TableDropdown gates its
  //     button, scroll handler and throttled loader on that flag, so the dropdown can
  //     never page past its first page again;
  //   - a superseded search strands isLoadingLocal, which CodingEntityDropdown feeds
  //     into `loading` — a permanent CircularProgress on the input, and TableDropdown
  //     renders "Loading options..." in place of "No options found" for every empty
  //     search afterwards.
  //
  // The second is reachable because nothing resets localNextToken when a search
  // starts, so hasMore stays true while one is in flight, and every load-more entry
  // point in TableDropdown gates on isLoadingMore alone rather than on `loading`.
  // Clicking Load more mid-search is therefore a live path, not a race.
  //
  // One helper rather than a line at each abort site, because the sites keep
  // multiplying and each new one has arrived without knowing the coupling.
  const supersedeInFlight = useCallback(() => {
    if (localFetchAbortRef.current) {
      localFetchAbortRef.current.abort();
    }
    setIsLoadingLocal(false);
    setIsLoadingMoreLocal(false);
  }, []);

  // Hydrate from cache on mount so re-appearing rows are instant
  useEffect(() => {
    if (!enabled || !paidolId) return;
    const key = makeCacheKey(codingType, paidolId, selectedGlCode?.id, selectedProject?.id, '', cardGroupId);
    const cached = getCached(key);
    if (cached) {
      setLocalItems(cached.items as T[]);
      setLocalNextToken(cached.nextToken);
      setLocalTotal(cached.total);
    }
  }, [enabled, paidolId, codingType, selectedGlCode?.id, selectedProject?.id, cardGroupId]);

  const fetchLocal = useCallback(
    async (term: string, nextToken?: string | null) => {
      if (!paidolId) return;

      const config = FLEX_SEARCH_CONFIGS[codingType as keyof typeof FLEX_SEARCH_CONFIGS];
      if (!config) return;

      const isLoadMore = !!nextToken;
      setSearchFailed(false);
      setSearchError(null);

      if (!isLoadMore) {
        // A new query invalidates the previous query's pagination. Neither of these was
        // reset before, so `hasMore` stayed true from the last query while a search was
        // in flight, and handleLoadMore reads localNextToken directly — clicking Load
        // more mid-search sent `filter: { searchTerm: 'xyz' }` with a nextToken minted
        // under `searchTerm: ''`. Two queries' pagination crossed in one call: the
        // search was aborted, the load-more merged a page of the UNFILTERED set against
        // an empty cache['xyz'], and TableDropdown then filtered those rows out locally
        // by term — so a search with real matches rendered "No options found", and the
        // mismatched page was cached under the search key.
        //
        // Clearing them removes the trigger rather than guarding its symptoms, which is
        // what the previous two fixes did downstream. It also makes the affordance
        // honest: hasMore is false while a query is in flight, because there is no valid
        // token for the term being searched until it returns. Every path that sets these
        // does so in the same batch as the items they belong to, so this does not flicker.
        setLocalNextToken(null);
        setLocalTotal(undefined);
      }
      const cacheKey = makeCacheKey(
        codingType,
        paidolId,
        selectedGlCode?.id,
        selectedProject?.id,
        term,
        cardGroupId
      );

      // Cache check for non-loadMore requests
      if (!isLoadMore) {
        const cached = getCached(cacheKey);
        if (cached) {
          // Serving from cache supersedes whatever is still in flight on this
          // instance. This was the last exit from fetchLocal that registered no
          // staleness token: clearing the box (or closing the dropdown, which is
          // wired to handleClearSearch) rendered the cached list and left the
          // in-flight search live, so a moment later it applied its own items — or
          // its failure — over the list the user had just cleared back to.
          //
          // Safe to abort here only because the shared promise no longer consults
          // this controller (see fetchPromise below). While it did, aborting on
          // behalf of one instance emptied the result for every instance joined to
          // the same key.
          supersedeInFlight();
          setLocalItems(cached.items as T[]);
          setLocalNextToken(cached.nextToken);
          setLocalTotal(cached.total);
          setIsLoadingLocal(false);
          return;
        }

        // In-flight dedup: another hook instance is already fetching this exact key
        const inflight = inflightRequests.get(cacheKey);
        if (inflight) {
          // Supersede BEFORE raising this join's spinner: the helper clears both
          // loading flags, so doing it the other way round would clear the `true`
          // this branch just set and the join would render without a spinner.
          //
          // A joiner needs its own staleness token, for the same reason the owning
          // path below has one: it awaits a promise it does not control, and by the
          // time that settles this instance may have moved on to a newer search.
          // A pure joiner never touched localFetchAbortRef, so there was nothing for
          // the next fetchLocal to abort and nothing here to check — the shared
          // result applied unconditionally, re-raising a failure over the newer
          // search's results, overwriting its items, and clearing its spinner.
          supersedeInFlight();
          setIsLoadingLocal(true);
          const joinController = new AbortController();
          localFetchAbortRef.current = joinController;

          const outcome = await inflight;
          // Superseded: the newer request owns the state now, including the spinner,
          // so this returns without clearing it.
          if (joinController.signal.aborted) return;

          if (outcome.entry) {
            setLocalItems(outcome.entry.items as T[]);
            setLocalNextToken(outcome.entry.nextToken);
            setLocalTotal(outcome.entry.total);
          } else if (outcome.failed) {
            // SEE-1392: the shared fetch belongs to whichever instance created it,
            // so its setSearchFailed only ever moved that instance's state. Without
            // this branch every dropdown that joined the dedup showed "No options
            // found" on a denial while the originating one showed the error copy —
            // the empty-vs-error confusion this ticket exists to remove, on the
            // path that covers most of the dropdowns on a transactions table.
            setSearchFailed(true);
            setSearchError(outcome.error);
          } else {
            setLocalItems([]);
          }
          setIsLoadingLocal(false);
          return;
        }
      }

      supersedeInFlight();
      const controller = new AbortController();
      localFetchAbortRef.current = controller;

      if (isLoadMore) {
        setIsLoadingMoreLocal(true);
      } else {
        setIsLoadingLocal(true);
      }

      // Snapshot the load-more merge base NOW, not when the response lands.
      //
      // The merge used to read `getCached(cacheKey)?.items ?? []` inside the promise.
      // getFlexCached returns null once the entry passes its 5-minute TTL — and it
      // does not refresh `timestamp` on a read, so an entry keeps aging while it is
      // being served. Leave a dropdown open past the TTL, click Load more, and the
      // base was `[]`: page 2 REPLACED the page 1 the user was looking at, and the
      // truncated list was written back to the module cache with a fresh timestamp,
      // so every other dropdown of that coding type on the page inherited it. hasMore
      // still read true, so nothing signalled the loss.
      //
      // The cache is used only when it is the SAME generation as the token being sent,
      // i.e. its own nextToken is the one we are about to request. An earlier version of
      // this line preferred the cache unconditionally and claimed that reading at call
      // time pinned the base to the state the token was minted against. It pins the
      // timing, not the generation, and two fixes on this branch make them diverge on a
      // single instance: the shared promise always writes the cache even when its caller
      // aborted, and a cache hit aborts an in-flight load-more.
      //
      // So: page 1 cached at 25 / tok-1 / total 50, click Load more, click back into the
      // search box (handleFocus → cache hit → abort). The load-more still publishes
      // 50 / tok-2 to the cache while local state stays 25 / tok-1. hasMore is 25 < 50,
      // so a second Load more sent tok-1 against a 50-item base and produced 75 items
      // with rows 26-50 duplicated — written back to the shared cache and inherited by
      // every sibling dropdown, with duplicate ids colliding as React keys.
      //
      // Falling back to the rendered items is right in that case: they are exactly what
      // this instance's token was minted against. It also covers the expired/evicted
      // entry, where getCached returns null.
      const cachedForLoadMore = isLoadMore ? getCached(cacheKey) : null;
      const loadMoreBase: unknown[] = isLoadMore
        ? ((cachedForLoadMore && cachedForLoadMore.nextToken === nextToken
            ? cachedForLoadMore.items
            : localItemsRef.current) as unknown[])
        : [];

      // Create the fetch promise and register it for dedup.
      //
      // This deliberately does NOT consult `controller.signal.aborted`. The promise is
      // shared through flexInflightRequests, so gating its RESULT on the originating
      // instance's controller lets one instance poison every instance joined to it —
      // and an instance can join its own request: the mount effect fires
      // `fetchLocal('')`, then onFocus fires `fetchLocal(searchTerm ?? '')`, same key,
      // and the cache is still empty because it is only written on success. The dedup
      // branch then aborts the controller belonging to the very promise it is about to
      // await, the promise reports an empty outcome, skips setCached, and every row in
      // the column renders "No options found" over real data on the first click after
      // page load.
      //
      // So the shared promise always reports the true outcome and always writes the
      // cache. Staleness is each consumer's business, checked before its own setState:
      // the owner at `controller.signal.aborted` below, the joiner at
      // `joinController.signal.aborted` above.
      const fetchPromise = (async (): Promise<FlexCodingFetchOutcome> => {
        try {
          // Before the request, not in the catch. TransactionService.execute captures
          // and rethrows, so it is what sends the report; a breadcrumb added after
          // that would attach to the NEXT captured event instead — on a dropdown that
          // retries per keystroke, usually a different search's codingType/paidolId.
          // In the buffer beforehand it reaches the report it describes, and it covers
          // the success case too, which is what makes a trail readable.
          addBreadcrumb({
            category: 'flexCoding',
            level: 'info',
            message: 'flex coding dropdown search',
            data: { codingType, paidolId, isLoadMore },
          });
          // SEE-301 Phase B: CDK AppSync via transactionService, through the per-coding-type
          // entry in FLEX_SEARCH_CONFIGS. The payload arrives unwrapped, so there is no
          // `dataKey` indirection any more.
          const data = (await config.search({
            limit: FLEX_PAGE_LIMIT,
            filter: {
              paidolId: { eq: paidolId },
              glCodeId: { eq: selectedGlCode?.id },
              flexCodeId: { eq: selectedProject?.id },
              ...(cardGroupId ? { cardGroupId } : {}),
              ...(term ? { searchTerm: term } : {}),
            },
            nextToken: nextToken || undefined,
          })) as { items: T[]; nextToken: string | null; total?: number } | null;

          if (data) {
            let items: unknown[];
            if (isLoadMore) {
              items = [...loadMoreBase, ...(data.items || [])];
            } else {
              items = (data.items || []) as unknown[];
            }

            const entry: CacheEntry = {
              items,
              nextToken: data.nextToken || null,
              total: data.total,
              timestamp: Date.now(),
            };
            // A page-1 promise publishes unconditionally: it is registered in
            // inflightRequests, joiners are awaiting it, and suppressing its write is
            // what caused the column-wide empty this branch already fixed once.
            //
            // A load-more publishes only if the cache is still the generation its page
            // composes onto — the same test loadMoreBase makes above. Publishing
            // unconditionally is unordered against whatever replaced the entry: the
            // merged list lands with a fresh timestamp over a newer one, and every
            // sibling dropdown sharing the key then serves the pre-refresh list for up
            // to the full TTL, so a code removed by a re-import stays selectable.
            //
            // Keyed on generation rather than on this caller's abort, because abort is
            // the wrong proxy in both directions. A load-more is never registered in
            // inflightRequests, so a sibling that misses the expired entry issues its
            // OWN page-1 request rather than joining, and nothing aborts this one — it
            // would publish stale data un-aborted. Conversely an abort caused by a new
            // search leaves the entry untouched and still composable, so suppressing
            // that write only throws away good work.
            //
            // Absent is not the same as superseded. If the entry merely expired and
            // nobody replaced it there is nothing to clobber, and the merged list is
            // built from a base snapshotted while it was still valid — so it publishes.
            // Treating absent as superseded would drop that write and leave the cache
            // empty after a load-more across a TTL boundary.
            const cachedAtPublish = isLoadMore ? getCached(cacheKey) : null;
            if (!isLoadMore || !cachedAtPublish || cachedAtPublish.nextToken === nextToken) {
              setCached(cacheKey, entry);
            }
            return { entry, failed: false, error: null };
          }
          return { entry: null, failed: false, error: null };
        } catch (error) {
          // SEE-1392: no longer a swallow. Reporting `failed` is what lets the dropdown
          // say the search failed rather than "No options found" — without it the
          // caller's `setLocalItems([])` made a denial indistinguishable from a search
          // with no matches.
          //
          // The item-list handling DID change, contrary to what this comment used to
          // claim: the caller now takes the `failed` branch instead of
          // `setLocalItems([])`, so a failed search keeps the previous query's rows
          // where develop cleared them. That is deliberate — blanking a list the user
          // is reading because a refresh failed is worse than leaving it — but it is a
          // behaviour change, not a no-op. There was never a spinner risk; `loading`
          // clears in the `finally` regardless.
          //
          // Nothing is captured here on purpose: TransactionService.execute already
          // captures and rethrows, so a second call sent every flex failure to Sentry
          // twice. The context that report needs is left as a breadcrumb before the
          // request instead, for the ordering reason described there.
          return {
            entry: null,
            failed: true,
            error: error instanceof Error ? error.message : null,
          };
        }
      })();

      if (!isLoadMore) {
        inflightRequests.set(cacheKey, fetchPromise);
      }

      try {
        const outcome = await fetchPromise;
        if (controller.signal.aborted) return;

        if (outcome.entry) {
          setLocalItems(outcome.entry.items as T[]);
          setLocalNextToken(outcome.entry.nextToken);
          setLocalTotal(outcome.entry.total);
        } else if (outcome.failed && !isLoadMore) {
          // Deliberately not raised for a load-more. searchFailed is the search's
          // empty-vs-error discriminator, and CodingEntityDropdown feeds it to both
          // `error` and `renderEmpty`. A failed page 2 would therefore paint the input
          // red while 25 good options sit selectable beneath it, and — because
          // TableDropdown uses renderEmpty for ANY empty list, not just an errored one
          // — the next term with no local match would read "We couldn't load these
          // options" instead of "No options found". That is the confusion SEE-1392
          // exists to remove, inverted. The failure still reaches Sentry via
          // captureException; surfacing page-2 failures needs its own affordance.
          setSearchFailed(true);
          setSearchError(outcome.error);
        } else if (!isLoadMore) {
          setLocalItems([]);
        }
      } finally {
        // Registration above is guarded by `!isLoadMore`, so deregistration must be
        // too — a load-more shares the base cacheKey and was otherwise deleting a
        // dedup entry another instance owned, sending a later joiner to the network
        // for a request already in flight.
        if (!isLoadMore) {
          inflightRequests.delete(cacheKey);
        }
        if (!controller.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMoreLocal(false);
          } else {
            setIsLoadingLocal(false);
          }
        }
      }
    },
    [paidolId, selectedGlCode?.id, selectedProject?.id, codingType, cardGroupId, supersedeInFlight]
  );

  // --- Initial load on mount ---

  useEffect(() => {
    if (!enabled || !paidolId || hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    fetchLocal('');
  }, [enabled, paidolId, fetchLocal]);

  // --- Re-fetch when filter IDs change ---

  const prevFiltersRef = useRef<{
    glCodeId: string | undefined;
    flexCodeId: string | undefined;
    cardGroupId: string | undefined;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !paidolId) return;

    const current = {
      glCodeId: selectedGlCode?.id,
      flexCodeId: selectedProject?.id,
      cardGroupId,
    };

    if (prevFiltersRef.current === null) {
      prevFiltersRef.current = current;
      return; // initial mount handled by the effect above
    }

    const prev = prevFiltersRef.current;
    if (
      prev.glCodeId !== current.glCodeId ||
      prev.flexCodeId !== current.flexCodeId ||
      prev.cardGroupId !== current.cardGroupId
    ) {
      prevFiltersRef.current = current;
      setLocalItems([]);
      setLocalNextToken(null);
      setLocalTotal(undefined);
      fetchLocal(searchTerm ?? '');
    }
  }, [enabled, paidolId, selectedGlCode?.id, selectedProject?.id, cardGroupId, fetchLocal, searchTerm]);

  // --- Reset on disable / unmount ---

  useEffect(() => {
    if (!enabled) {
      hasInitializedRef.current = false;
      prevFiltersRef.current = null;
      setSearchTerm(undefined);
      setLocalItems([]);
      setLocalNextToken(null);
      setLocalTotal(undefined);
      // This branch already resets everything else it owns; leaving the failure behind
      // meant a dropdown disabled while showing the error state kept `error` styling
      // with no way to retry, since fetchLocal only clears these on the next fetch.
      setSearchFailed(false);
      setSearchError(null);
    }
    return () => {
      hasInitializedRef.current = false;
      prevFiltersRef.current = null;
      // Routed through the helper rather than aborting directly. On unmount the state
      // resets are moot, but this same effect also runs when the dropdown is DISABLED
      // mid-flight (clearing a project disables its cost-code dropdown), and then the
      // spinners would strand on a still-rendered input. It self-heals on re-enable via
      // the mount effect, but the helper's comment claims nothing else clears these —
      // this was the site that made that false.
      supersedeInFlight();
    };
  }, [enabled, codingType, supersedeInFlight]);

  // --- Search / focus / load handlers ---

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term || undefined);
      fetchLocal(term);
    },
    [fetchLocal]
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm(undefined);
    fetchLocal('');
  }, [fetchLocal]);

  const handleFocus = useCallback(() => {
    if (!paidolId) return;
    fetchLocal(searchTerm ?? '');
  }, [paidolId, fetchLocal, searchTerm]);

  const loadOptions = useCallback(() => {
    if (!paidolId) return;
    fetchLocal('');
  }, [paidolId, fetchLocal]);

  const handleLoadMore = useCallback(async () => {
    if (!localNextToken) return;
    await fetchLocal(searchTerm ?? '', localNextToken);
  }, [localNextToken, fetchLocal, searchTerm]);

  // --- Derived state ---

  const hasMore = useMemo(() => {
    // The token is authoritative: null means the backend has no further page, whatever
    // `total` reports. The total-based branch alone rendered a permanent "Load more"
    // row whenever total exceeded the item count with no token — which the composite
    // aggregation paths can produce, since they count matching relationship rows while
    // returning distinct entities. handleLoadMore short-circuits on a null token, so
    // that row did nothing when clicked.
    if (!localNextToken) return false;
    if (localTotal !== undefined && localItems.length > 0) {
      return localItems.length < localTotal;
    }
    return true;
  }, [localItems, localTotal, localNextToken]);

  return {
    currentItems: localItems,
    isSearching: isLoadingLocal,
    isLoadingMore: isLoadingMoreLocal,
    searchFailed,
    searchError,
    hasMore,
    searchTerm,
    handleSearch,
    handleClearSearch,
    handleFocus,
    handleLoadMore,
    loadOptions,
  };
};
